import { useMemo } from "react";

export type InspectionItemType = "pass_fail" | "numeric" | "text" | "photo_required" | "select";

export interface InspectionTemplateDraftItem {
  id: string;
  label: string;
  itemType: InspectionItemType;
  options: string[];
  isRequired: boolean;
  groupName: string;
}

export interface InspectionTemplateDraft {
  name: string;
  description: string;
  serviceId: string;
  phase: "pre_work" | "post_work" | "quality_assurance";
  isRequired: boolean;
  items: InspectionTemplateDraftItem[];
}

interface ServiceOption {
  id: string;
  name: string;
}

interface InspectionFormProps {
  draft: InspectionTemplateDraft;
  services: ServiceOption[];
  saving: boolean;
  onChange(next: InspectionTemplateDraft): void;
  onSubmit(): void;
}

const ITEM_TYPE_LABELS: Record<InspectionItemType, string> = {
  pass_fail: "Pass / Fail / N.A.",
  numeric: "Numeric measurement",
  text: "Text note",
  photo_required: "Required photo",
  select: "Select options",
};

export function createEmptyInspectionItem(): InspectionTemplateDraftItem {
  return {
    id: crypto.randomUUID(),
    label: "",
    itemType: "pass_fail",
    options: [],
    isRequired: true,
    groupName: "",
  };
}

export function createEmptyInspectionDraft(): InspectionTemplateDraft {
  return {
    name: "",
    description: "",
    serviceId: "",
    phase: "pre_work",
    isRequired: false,
    items: [createEmptyInspectionItem()],
  };
}

export function InspectionForm({
  draft,
  services,
  saving,
  onChange,
  onSubmit,
}: InspectionFormProps) {
  const canSubmit = useMemo(
    () => draft.name.trim().length > 0 && draft.items.every((item) => item.label.trim().length > 0),
    [draft],
  );

  function updateItem(id: string, updater: (item: InspectionTemplateDraftItem) => InspectionTemplateDraftItem) {
    onChange({
      ...draft,
      items: draft.items.map((item) => item.id === id ? updater(item) : item),
    });
  }

  return (
    <div className="space-y-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-primary)]">Inspection template</p>
        <h2 className="mt-2 text-xl font-semibold text-[var(--color-foreground)]">Create checklist template</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Build the exact checklist technicians complete on live bookings. Required photo items and grouped sections are enforced here, not inferred later.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Template name</span>
          <input
            value={draft.name}
            onChange={(event) => onChange({ ...draft, name: event.target.value })}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Service scope</span>
          <select
            value={draft.serviceId}
            onChange={(event) => onChange({ ...draft, serviceId: event.target.value })}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
          >
            <option value="">All services</option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>{service.name}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-sm md:col-span-2">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Description</span>
          <textarea
            value={draft.description}
            onChange={(event) => onChange({ ...draft, description: event.target.value })}
            rows={3}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Phase</span>
          <select
            value={draft.phase}
            onChange={(event) => onChange({ ...draft, phase: event.target.value as InspectionTemplateDraft["phase"] })}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
          >
            <option value="pre_work">Pre-work</option>
            <option value="post_work">Post-work</option>
            <option value="quality_assurance">Quality assurance</option>
          </select>
        </label>
        <label className="flex items-center gap-2 pt-7 text-sm text-[var(--color-foreground)]">
          <input
            type="checkbox"
            checked={draft.isRequired}
            onChange={(event) => onChange({ ...draft, isRequired: event.target.checked })}
          />
          Required before booking completion
        </label>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Checklist items</h3>
            <p className="text-sm text-[var(--color-muted-foreground)]">Define the exact fields technicians must complete.</p>
          </div>
          <button
            type="button"
            onClick={() => onChange({ ...draft, items: [...draft.items, createEmptyInspectionItem()] })}
            className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
          >
            Add item
          </button>
        </div>

        {draft.items.map((item, index) => (
          <div key={item.id} className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-[var(--color-foreground)]">Item {index + 1}</p>
              {draft.items.length > 1 && (
                <button
                  type="button"
                  onClick={() => onChange({ ...draft, items: draft.items.filter((entry) => entry.id !== item.id) })}
                  className="text-xs font-medium text-[var(--color-destructive)]"
                >
                  Remove
                </button>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Label</span>
                <input
                  value={item.label}
                  onChange={(event) => updateItem(item.id, (current) => ({ ...current, label: event.target.value }))}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Item type</span>
                <select
                  value={item.itemType}
                  onChange={(event) => updateItem(item.id, (current) => ({ ...current, itemType: event.target.value as InspectionItemType }))}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                >
                  {Object.entries(ITEM_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Group label</span>
                <input
                  value={item.groupName}
                  onChange={(event) => updateItem(item.id, (current) => ({ ...current, groupName: event.target.value }))}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                />
              </label>
              <label className="flex items-center gap-2 pt-7 text-sm text-[var(--color-foreground)]">
                <input
                  type="checkbox"
                  checked={item.isRequired}
                  onChange={(event) => updateItem(item.id, (current) => ({ ...current, isRequired: event.target.checked }))}
                />
                Required item
              </label>
            </div>
            {item.itemType === "select" && (
              <label className="space-y-1 text-sm">
                <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Options</span>
                <input
                  value={item.options.join(", ")}
                  onChange={(event) => updateItem(item.id, (current) => ({
                    ...current,
                    options: event.target.value.split(",").map((entry) => entry.trim()).filter(Boolean),
                  }))}
                  placeholder="Pass, Fail, Needs review"
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                />
              </label>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!canSubmit || saving}
          onClick={onSubmit}
          className="rounded-xl bg-[var(--color-primary)] px-4 py-3 text-sm font-semibold text-[var(--color-primary-foreground)] disabled:opacity-60"
        >
          {saving ? "Saving template..." : "Save template"}
        </button>
      </div>
    </div>
  );
}
